import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  baseHedgeMaterialsService, 
  CreateDerivedMaterialPayload,
  UpdateDerivedMaterialPayload
} from '../services/baseHedgeMaterialsService';

export const useBaseHedgeMaterials = (accountId: number | undefined) => {
  const result = useQuery({
    queryKey: ['baseHedgeMaterials', accountId],
    queryFn: async () => {
      const data = await baseHedgeMaterialsService.getByAccount(accountId!);
      return data;
    },
    enabled: !!accountId,
  });
  
  return result;
};

export const useDerivedMaterials = (baseHedgeMaterialId: number | undefined) => {
  return useQuery({
    queryKey: ['derivedMaterials', baseHedgeMaterialId],
    queryFn: () => baseHedgeMaterialsService.getDerivedMaterials(baseHedgeMaterialId!),
    enabled: !!baseHedgeMaterialId,
  });
};

export const useCreateDerivedMaterial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ baseHedgeMaterialId, payload }: { 
      baseHedgeMaterialId: number; 
      payload: CreateDerivedMaterialPayload 
    }) => baseHedgeMaterialsService.createDerivedMaterial(baseHedgeMaterialId, payload),
    retry: false, // Don't retry failed mutations
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['derivedMaterials', variables.baseHedgeMaterialId] });
      queryClient.invalidateQueries({ queryKey: ['hedge-derivation-allocations'] });
      queryClient.invalidateQueries({ queryKey: ['hedge-positions'] });
    },
    onError: (error) => {
      // Mutation failed
    },
  });
};

export const useUpdateDerivedMaterial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ derivedMaterialId, payload }: { 
      derivedMaterialId: number; 
      payload: UpdateDerivedMaterialPayload 
    }) => baseHedgeMaterialsService.updateDerivedMaterial(derivedMaterialId, payload),
    onSuccess: () => {
      // The response has no base-material id, so refresh every derived-materials list and
      // every view built on the derivation (policies, terminal coverage, Commodex table).
      queryClient.invalidateQueries({ queryKey: ['derivedMaterials'] });
      queryClient.invalidateQueries({ queryKey: ['hedge-derivation-allocations'] });
      queryClient.invalidateQueries({ queryKey: ['hedge-positions'] });
      queryClient.invalidateQueries({ queryKey: ['terminal-coverage-derivation'] });
      queryClient.invalidateQueries({ queryKey: ['commodex-derivation'] });
    },
  });
};

export const useDeleteDerivedMaterial = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ derivedMaterialId, baseHedgeMaterialId }: { 
      derivedMaterialId: number;
      baseHedgeMaterialId: number;
    }) => baseHedgeMaterialsService.deleteDerivedMaterial(derivedMaterialId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['derivedMaterials', variables.baseHedgeMaterialId] });
      queryClient.invalidateQueries({ queryKey: ['hedge-derivation-allocations'] });
      queryClient.invalidateQueries({ queryKey: ['hedge-positions'] });
    },
  });
};
